import get from 'lodash.get';
import { Component } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { IntlContext } from 'dibs-react-intl';
import validate from 'dibs-credit-card-validator';
import { getCardType } from '../../utils/helpers';
import { Fields } from '../../utils/constants';
import styles from '../../styles/main.scss';

// components
import CardHolderName from '../fields/CardHolderName';
import CardNumber from '../fields/CardNumber';
import { CardLogo } from '../fields/CardLogo';
import SecurityCode from '../fields/SecurityCode';
import { Expiry } from '../fields/Expiry';
import Submit from '../inputs/Submit';

class CreditCardForm extends Component {
    constructor(props) {
        super(props);
        const { creditCard, fields, disabled, isVerifying } = props;

        this.onChange = this.onChange.bind(this);
        this.onBlur = this.onBlur.bind(this);
        this.onSubmit = this.onSubmit.bind(this);
        this.onFocusCreditCard = this.onFocusCreditCard.bind(this);

        this.state = {
            creditCard: fields.reduce((o, f) => ({ ...o, [f]: creditCard[f] || '' }), {}),
            blurred: fields.reduce((o, f) => ({ ...o, [f]: isVerifying || !!creditCard[f] }), {}),
            disabled: fields.reduce((o, f) => ({ ...o, [f]: disabled.includes(f) }), {}),
            isSaving: false,
            showCCNumber: true,
        };
    }
    componentDidMount() {
        if (this.props.onMount) {
            this.props.onMount();
        }
    }
    onValidate() {
        const { creditCard } = this.state;
        const { validations } = this.props;
        return validate(creditCard, validations);
    }
    onChange({ name, value }, cb = () => {}) {
        const { onUpdate } = this.props;
        const creditCard = { ...this.state.creditCard, [name]: value };
        if (onUpdate) {
            onUpdate({ creditCard, field: name, value });
        }
        this.setState(
            {
                creditCard,
            },
            () => cb({ name, value })
        );
    }
    onBlur({ name, value = '' }) {
        const { onBlur } = this.props;
        const blurred = { ...this.state.blurred, [name]: true };
        if (onBlur) {
            const { errors = {} } = this.onValidate();
            const error = get(errors[name], 'defaultMessage') || errors[name] || null;
            onBlur({ field: name, value, error });
        }
        if (name === 'creditCardNumber') {
            this.setState({ showCCNumber: false });
        }
        this.setState({ blurred });
    }
    onFocusCreditCard() {
        this.setState({ showCCNumber: true });
    }
    onSubmit(isValid, errors) {
        const { creditCard } = this.state;
        const { hideSubmit, disableSubmit, onSubmit } = this.props;
        const onError = () => this.setState({ isSaving: false });

        onSubmit({ creditCard, isValid, errors, onError });

        if (!disableSubmit && isValid && !hideSubmit) {
            this.setState({ isSaving: true });
        } else if (!hideSubmit) {
            this.setState({
                blurred: Object.keys(creditCard).reduce(
                    (obj, field) => ({ ...obj, [field]: true }),
                    {}
                ),
            });
        }
    }
    render() {
        const { creditCard, blurred, disabled, isSaving, showCCNumber } = this.state;
        const {
            wrapperClass,
            formClass,
            theme,
            autoFocus,
            validations,
            hideSubmit,
            submitLabel,
            disableSubmit,
            disableForm,
            renderChildren,
            showAllErrors,
            fullWidth,
            maskCreditCardNumber,
        } = this.props;
        const { isValid, errors } = this.onValidate();

        const hideCCNumber = maskCreditCardNumber && !showCCNumber && !errors.creditCardNumber;

        const wrapperClasses = classNames(wrapperClass, theme.wrapper);
        const formClasses = classNames(formClass, theme.form);

        const props = Object.keys(creditCard).reduce(
            (obj, field) => ({
                ...obj,
                [field]: {
                    value:
                        field === 'creditCardNumber' && hideCCNumber
                            ? creditCard[field].replace(/[0-9](?=.{4})/g, '\u2027')
                            : creditCard[field],
                    error: errors[field] ? this.context.formatMessage(errors[field]) : null,
                    isBlurred: showAllErrors || blurred[field],
                    disabled: isSaving || disableForm || disabled[field],
                    onChange: this.onChange,
                    onBlur: this.onBlur,
                    onFocus: this.onFocusCreditCard,
                    fullWidth,
                },
            }),
            {}
        );
        const cardType = getCardType(creditCard.creditCardNumber);
        return (
            <div className={wrapperClasses} data-tn="dibs-credit-card-form-wrapper">
                <form className={formClasses}>
                    <div className={styles.group}>
                        <CardNumber
                            {...props.creditCardNumber}
                            autoFocus={autoFocus}
                            leftDecorator={
                                <CardLogo
                                    cardType={cardType}
                                    validations={validations}
                                    disabled={props.creditCardNumber.disabled}
                                />
                            }
                        />
                    </div>
                    <div className={styles.group}>
                        <CardHolderName {...props.cardHolderName} />
                    </div>
                    <div className={styles.group}>
                        <div
                            className={classNames(styles.left, {
                                [styles.fullWidth]: fullWidth,
                            })}
                        >
                            <Expiry {...props.cardExpiry} />
                            <SecurityCode {...props.securityCode} />
                        </div>
                    </div>
                </form>
                {renderChildren({ creditCard, errors, isValid })}
                {!hideSubmit && (
                    <Submit
                        label={submitLabel}
                        disabled={disableSubmit || isSaving || !isValid}
                        wrapperClass={theme.submit}
                        isSaving={isSaving}
                        onClick={() => this.onSubmit(isValid, errors)}
                    />
                )}
            </div>
        );
    }
}

CreditCardForm.contextType = IntlContext;

CreditCardForm.propTypes = {
    wrapperClass: PropTypes.string,
    formClass: PropTypes.string,
    theme: PropTypes.object,
    creditCard: PropTypes.object,
    renderChildren: PropTypes.func,
    fields: PropTypes.arrayOf(PropTypes.string),
    disabled: PropTypes.array,
    validations: PropTypes.object,
    autoFocus: PropTypes.bool,
    disableForm: PropTypes.bool,
    hideSubmit: PropTypes.bool,
    submitLabel: PropTypes.node,
    disableSubmit: PropTypes.bool,
    onMount: PropTypes.func,
    onUpdate: PropTypes.func,
    onBlur: PropTypes.func,
    onSubmit: PropTypes.func,
    showAllErrors: PropTypes.bool,
    isVerifying: PropTypes.bool,
    fullWidth: PropTypes.bool,
    maskCreditCardNumber: PropTypes.bool,
};

CreditCardForm.defaultProps = {
    wrapperClass: '',
    formClass: '',
    theme: {},
    creditCard: {},
    renderChildren: () => null,
    fields: Fields,
    disabled: [],
    validations: {},
    autoFocus: false,
    showCityAndState: false,
    hideCompany: false,
    hidePhoneNumber: false,
    hideSubmit: false,
    disableForm: false,
    disableSubmit: false,
    onSubmit: () => null,
    showAllErrors: false,
    isVerifying: false,
    fullWidth: false,
    maskCreditCardNumber: false,
};

export default CreditCardForm;
